import { Box } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { DateTime } from 'luxon';

import { Banner, BannerScope, BannerStatus, BannerType } from '../../../models/BannersModels';

import ClickableLink from '../../../components/Text/ClickableLink';
import BannerStatusTag from '../../../components/StatusTag/BannerStatusTag';
import { ReviewRow, ReviewLabel, ReviewValue, ReviewChip } from './BannerReviewContentStyle';
import useMessagePreview, { supportedLanguages } from './useMessagePreview';

interface BannerReviewContentProps {
  formData: Omit<Banner, 'id' | 'status' | 'createdBy'>;
  marketNameById: Map<string, string>;
  companyNameById: Map<string, string>;
  status?: BannerStatus;
  isPreview?: boolean;
}

function BannerReviewContent({
  formData,
  marketNameById,
  companyNameById,
  status,
  isPreview = false,
}: BannerReviewContentProps) {
  const [t] = useTranslation();

  const bannerTypeLabels: Record<BannerType, string> = {
    [BannerType.PLANNED_CHANGE]: t('pages.bannerOnboarding.bannerType.plannedChange'),
    [BannerType.OUTAGE]: t('pages.bannerOnboarding.bannerType.outage'),
    [BannerType.OTHER]: t('pages.bannerOnboarding.bannerType.other'),
  };

  const scopeLabels: Record<BannerScope, string> = {
    [BannerScope.GLOBAL]: t('pages.bannerOnboarding.scope.global'),
    [BannerScope.MARKETS]: t('pages.bannerOnboarding.scope.markets'),
    [BannerScope.COMPANIES]: t('pages.bannerOnboarding.scope.companies'),
  };

  let scopeTargetNames: string[] = [];
  if (formData.scope === BannerScope.MARKETS) {
    scopeTargetNames = formData.selectedMarkets.map((id) => marketNameById.get(id) ?? id);
  } else if (formData.scope === BannerScope.COMPANIES) {
    scopeTargetNames = formData.selectedCompanies.map((id) => companyNameById.get(id) ?? id);
  }

  const isTemplatedType =
    formData.bannerType === BannerType.PLANNED_CHANGE || formData.bannerType === BannerType.OUTAGE;
  const messageLanguages = isTemplatedType
    ? supportedLanguages
    : supportedLanguages.filter((language) => Boolean(formData.messages[language]));

  /**
   * Converts an ISO date string into a DateTime for the message preview
   * @param value
   */
  const toDateTimeValue = (value: string) => (value ? DateTime.fromISO(value) : null);

  const { templateKey, fixedT, startDateText, endDateText } = useMessagePreview({
    bannerType: formData.bannerType,
    startDate: toDateTimeValue(formData.maintenanceStart || formData.startDate),
    endDate: toDateTimeValue(formData.maintenanceEnd || formData.endDate),
  });

  /**
   * Formats an ISO date string for display, e.g. "13 July 2026, 09:00"
   * @param iso
   */
  const formatDateTime = (iso: string) =>
    DateTime.fromISO(iso).setLocale(i18next.language).toFormat('d MMMM yyyy, HH:mm');

  /**
   * Formats a date range for display, e.g. "13 July 2026, 09:00 to 14 July 2026, 18:00"
   * @param start
   * @param end
   */
  const formatDateRange = (start: string, end: string) =>
    t('pages.bannerOnboarding.review.dateRange', { start: formatDateTime(start), end: formatDateTime(end) });

  /**
   * Formats the display schedule, falling back to "Immediate"/"until manually stopped" for whichever
   * end is left open, since both the start and end date are optional
   */
  const getDisplaySchedule = () => {
    const startLabel = formData.startDate
      ? formatDateTime(formData.startDate)
      : t('pages.bannerOnboarding.review.immediate');

    if (formData.endDate) {
      return t('pages.bannerOnboarding.review.dateRange', { start: startLabel, end: formatDateTime(formData.endDate) });
    }
    return t('pages.bannerOnboarding.review.untilManuallyStopped', { start: startLabel });
  };

  return (
    <>
      {isPreview && status ? (
        <ReviewRow>
          <ReviewLabel>{t('pages.banners.table.headers.status')}</ReviewLabel>
          <ReviewValue>
            <BannerStatusTag status={status} label={t(`pages.banners.table.status.${status}`)} />
          </ReviewValue>
        </ReviewRow>
      ) : null}

      <ReviewRow>
        <ReviewLabel>{t('pages.bannerOnboarding.review.type')}</ReviewLabel>
        <ReviewValue>{bannerTypeLabels[formData.bannerType as BannerType]}</ReviewValue>
      </ReviewRow>

      <ReviewRow>
        <ReviewLabel>{t('pages.bannerOnboarding.review.scope')}</ReviewLabel>
        <ReviewValue>
          {scopeLabels[formData.scope as BannerScope]}
          {scopeTargetNames.map((name) => (
            <ReviewChip key={name} label={name} />
          ))}
        </ReviewValue>
      </ReviewRow>

      <ReviewRow>
        <ReviewLabel>{t('pages.bannerOnboarding.schedule.bannerTitle')}</ReviewLabel>
        <ReviewValue>{formData.bannerTitle}</ReviewValue>
      </ReviewRow>

      <ReviewRow>
        <ReviewLabel>{t('pages.bannerOnboarding.schedule.title')}</ReviewLabel>
        <ReviewValue>{getDisplaySchedule()}</ReviewValue>
      </ReviewRow>

      {formData.bannerType === BannerType.PLANNED_CHANGE && formData.maintenanceStart && formData.maintenanceEnd ? (
        <ReviewRow>
          <ReviewLabel>{t('pages.bannerOnboarding.maintenanceWindow.title')}</ReviewLabel>
          <ReviewValue>{formatDateRange(formData.maintenanceStart, formData.maintenanceEnd)}</ReviewValue>
        </ReviewRow>
      ) : null}

      <ReviewRow>
        <ReviewLabel>{t('pages.bannerOnboarding.review.languages')}</ReviewLabel>
        <ReviewValue>
          {messageLanguages.map((language) => (
            <ReviewChip key={language} label={t(`common.languages.${language}`)} />
          ))}
        </ReviewValue>
      </ReviewRow>

      <ReviewRow>
        <ReviewLabel>{t('pages.bannerOnboarding.review.preview')}</ReviewLabel>
        <ReviewValue>
          <Box>
            {templateKey ? (
              <Trans
                t={fixedT}
                i18nKey={`pages.bannerOnboarding.message.templates.${templateKey}`}
                values={{ startDate: startDateText, endDate: endDateText }}
                tOptions={{ interpolation: { escapeValue: false } }}
                components={{ Link: <ClickableLink to="/dashboard" target="_blank" /> }}
              />
            ) : (
              formData.messages.en || t('pages.bannerOnboarding.message.previewPlaceholder')
            )}
          </Box>
        </ReviewValue>
      </ReviewRow>

      <ReviewRow>
        <ReviewLabel>{t('pages.bannerOnboarding.review.dismissible')}</ReviewLabel>
        <ReviewValue>
          {formData.dismissible
            ? t('pages.bannerOnboarding.review.dismissibleYes')
            : t('pages.bannerOnboarding.review.dismissibleNo')}
        </ReviewValue>
      </ReviewRow>
    </>
  );
}

export default BannerReviewContent;
